import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { ExpenseItemCard } from "@/components/expenses/ExpenseItemCard";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExpenseBankSelect } from "@/components/expenses/ExpenseBankSelect";
import { ExpenseSubcategorySelect } from "@/components/expenses/ExpenseSubcategorySelect";
import { toast } from "@/components/ui/sonner";
import { useGridPagination } from "@/hooks/useGridPagination";
import {
  createExpense,
  deleteExpense,
  EXPENSE_PAYMENT_METHODS,
  listExpenses,
  normalizeExpenseDate,
  paymentMethodMeta,
  paymentMethodUsesBank,
  syncExpensesFromApi,
  updateExpense,
  type ExpenseDraft,
  type ExpensePaymentMethod,
  type ExpenseRecord,
} from "@/lib/expenses/expensesStore";
import { listExpenseCategories, fetchExpenseSubcategories } from "@/lib/expenses/expenseCategoriesStore";
import { fetchBanks, type BankOption } from "@/lib/sales/banksStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import {
  CalendarIcon,
  Filter,
  LayoutGrid,
  List,
  Loader2,
  Plus,
  Receipt,
  Search,
} from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

type ExpenseSortBy = "latest" | "oldest" | "amount_asc" | "amount_desc" | "updated_latest" | "updated_oldest";

function expenseErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  return "Something went wrong. Please try again.";
}

const emptyDraft = (): ExpenseDraft => ({
  expenseCategoryId: "",
  date: format(new Date(), "yyyy-MM-dd"),
  amount: "",
  paymentMethod: "",
  bankId: "",
  referenceNo: "",
  chequeNo: "",
  chequeDate: "",
  description: "",
});

const Expenses = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "expense_can_create");
  const canUpdate = can(user, "expense_can_update");
  const canDelete = can(user, "expense_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<ExpenseSortBy>("latest");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [filterCategoryId, setFilterCategoryId] = useState<string>("all");
  const [filterPaymentMethod, setFilterPaymentMethod] = useState<"all" | ExpensePaymentMethod>("all");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseRecord | null>(null);
  const [draft, setDraft] = useState<ExpenseDraft>(emptyDraft());
  const [deleteTarget, setDeleteTarget] = useState<ExpenseRecord | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [banks, setBanks] = useState<BankOption[]>([]);

  const expenses = useMemo(() => listExpenses(), [version]);
  const [categories, setCategories] = useState(() => listExpenseCategories());
  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  const expenseLabels = (e: ExpenseRecord) => {
    const cat = categoryById.get(e.expenseCategoryId);
    const subcategory = (cat?.name || e.subcategoryName || "").trim();
    const category = (cat?.accountDetailTypeTitle || (e.subcategoryName ? e.categoryName : "") || "").trim();
    return {
      subcategory: subcategory || "Unassigned subcategory",
      category: category || "Uncategorized",
    };
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsBootstrapping(true);
      setIsForbidden(false);
      try {
        const [, options] = await Promise.all([
          syncExpensesFromApi(),
          fetchExpenseSubcategories().catch((error) => {
            if (isAxiosError(error) && error.response?.status === 403) {
              return listExpenseCategories();
            }
            console.error("[Expenses] Subcategories load failed", error);
            return listExpenseCategories();
          }),
        ]);
        void fetchBanks()
          .then((rows) => {
            if (mounted) setBanks(rows);
          })
          .catch(() => {
            if (mounted) setBanks([]);
          });
        if (mounted) setCategories(options);
        if (mounted) refresh();
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 403) {
          if (mounted) setIsForbidden(true);
          return;
        }
        console.error("[Expenses] Load failed", error);
        if (mounted) toast.error("Could not fetch expenses.");
      } finally {
        if (mounted) setIsBootstrapping(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const min = Number(minAmount);
    const max = Number(maxAmount);
    const hasMin = minAmount.trim() !== "" && Number.isFinite(min);
    const hasMax = maxAmount.trim() !== "" && Number.isFinite(max);

    return expenses.filter((e) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(e.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const expenseDate = parseISO(normalizeExpenseDate(e.date));
        if (!isValid(expenseDate)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (expenseDate < start || expenseDate > end) return false;
      }

      if (filterCategoryId !== "all" && e.expenseCategoryId !== filterCategoryId) return false;

      if (filterPaymentMethod !== "all" && e.paymentMethod !== filterPaymentMethod) return false;

      if (hasMin && e.amount < min) return false;
      if (hasMax && e.amount > max) return false;

      const labels = expenseLabels(e);
      const haystack = `${labels.subcategory} ${labels.category} ${e.description} ${e.date} ${e.amount}`.toLowerCase();
      return !q || haystack.includes(q);
    });
  }, [expenses, query, filterCreatedDate, quickDate, filterCategoryId, filterPaymentMethod, minAmount, maxAmount, categoryById]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "amount_asc":
          return a.amount - b.amount;
        case "amount_desc":
          return b.amount - a.amount;
        case "updated_oldest":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "updated_latest":
          return b.updatedAt.localeCompare(a.updatedAt);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterCreatedDate ? 1 : 0) +
    (filterCategoryId !== "all" ? 1 : 0) +
    (filterPaymentMethod !== "all" ? 1 : 0) +
    (minAmount.trim() ? 1 : 0) +
    (maxAmount.trim() ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterCategoryId("all");
    setFilterPaymentMethod("all");
    setMinAmount("");
    setMaxAmount("");
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterCategoryId}|${filterPaymentMethod}|${minAmount}|${maxAmount}`;
  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const openCreate = () => {
    setEditing(null);
    setDraft(emptyDraft());
    setModalOpen(true);
  };

  const openEdit = (e: ExpenseRecord) => {
    setEditing(e);
    setDraft({
      expenseCategoryId: e.expenseCategoryId,
      date: normalizeExpenseDate(e.date),
      amount: e.amount,
      paymentMethod: e.paymentMethod,
      bankId: e.bankId ?? "",
      referenceNo: e.referenceNo ?? "",
      chequeNo: e.chequeNo ?? "",
      chequeDate: e.chequeDate ?? "",
      description: e.description,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setDraft(emptyDraft());
  };

  const saveExpense = async () => {
    if (isSaving) return;
    if (editing && !canUpdate) {
      toast.error("You don’t have permission to edit expenses.");
      return;
    }
    if (!editing && !canCreate) {
      toast.error("You don’t have permission to create expenses.");
      return;
    }
    if (!draft.expenseCategoryId) {
      toast.error("Subcategory is required.");
      return;
    }
    if (!draft.date.trim()) {
      toast.error("Date is required.");
      return;
    }
    const amount = Number(draft.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Amount must be greater than 0.");
      return;
    }
    if (!draft.paymentMethod) {
      toast.error("Payment method is required.");
      return;
    }
    if (paymentMethodUsesBank(draft.paymentMethod) && !draft.bankId) {
      toast.error("Bank is required for this payment method.");
      return;
    }
    const methodMeta = paymentMethodMeta(draft.paymentMethod);
    if (draft.paymentMethod === "cheque" && !draft.chequeNo.trim()) {
      toast.error("Cheque # is required.");
      return;
    }
    if (methodMeta?.referenceLabel && draft.paymentMethod !== "cheque" && !draft.referenceNo.trim()) {
      toast.error(`${methodMeta.referenceLabel} is required.`);
      return;
    }

    setIsSaving(true);
    try {
      const payload: ExpenseDraft = {
        ...draft,
        amount,
        description: draft.description.trim(),
      };
      if (editing) {
        const result = await updateExpense(editing.id, payload);
        if (!result) {
          toast.error("Expense not found.");
          return;
        }
        toast.success(result.message);
      } else {
        const result = await createExpense(payload);
        toast.success(result.message);
      }
      closeModal();
      refresh();
    } catch (error) {
      console.error("[Expenses] Save failed", error);
      toast.error(expenseErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    if (!canDelete) {
      toast.error("You don’t have permission to delete expenses.");
      return;
    }
    setIsDeleting(true);
    try {
      const result = await deleteExpense(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
      toast.success(result.message);
    } catch (error) {
      console.error("[Expenses] Delete failed", error);
      toast.error(expenseErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  if (isForbidden) {
    return (
      <PermissionDenied
        title="Expenses access required"
        message="You don’t have permission to view expenses. Ask an administrator to grant expense_can_view_any."
      />
    );
  }

  return (
    <Layout title="Expenses">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Expenses</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Record and track business expenses.{" "}
              <Link to="/dashboard" className="text-primary hover:underline font-medium">
                Home
              </Link>
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate}>
              <Plus className="w-4 h-4" />
              Add Expense
            </Button>
          ) : null}
        </div>

        <Dialog
          open={modalOpen}
          onOpenChange={(o) => {
            if (!o) closeModal();
            else setModalOpen(true);
          }}
        >
          <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
            <DialogHeader className="pb-3 border-b border-border/50">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                  <Receipt className="w-5 h-5" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-bold text-foreground">
                    {editing ? "Edit expense" : "Record expense"}
                  </DialogTitle>
                  <p className="text-xs text-muted-foreground">
                    Post a payment voucher with the correct cash or bank channel.
                  </p>
                </div>
              </div>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <div className="space-y-2 md:col-span-2">
                <Label className="text-xs text-muted-foreground">
                  Subcategory <span className="text-destructive">*</span>
                </Label>
                <ExpenseSubcategorySelect
                  value={draft.expenseCategoryId}
                  subcategories={categories}
                  onChange={(id) => setDraft((d) => ({ ...d, expenseCategoryId: id }))}
                  disabled={categories.length === 0}
                />
                <p className="text-[11px] text-muted-foreground">
                  Manage subcategories in{" "}
                  <Link to="/expense-categories" className="text-primary hover:underline font-medium">
                    Expense categories
                  </Link>
                  .
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Date <span className="text-destructive">*</span>
                </Label>
                <DatePartsPicker
                  value={draft.date}
                  onChange={(iso) => setDraft((prev) => ({ ...prev, date: normalizeExpenseDate(iso) }))}
                  fromYear={1980}
                  toYear={new Date().getFullYear() + 1}
                  placeholder="Select date"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Amount <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  className="glass-input h-10"
                  value={draft.amount}
                  onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))}
                  placeholder="e.g. 2500"
                />
              </div>

              <div className={cn("space-y-2", paymentMethodUsesBank(draft.paymentMethod) && "md:col-span-2")}>
                <Label className="text-xs text-muted-foreground">
                  Payment method <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={draft.paymentMethod || undefined}
                  onValueChange={(v) => {
                    const method = v as ExpensePaymentMethod;
                    setDraft((d) => ({
                      ...d,
                      paymentMethod: method,
                      bankId: paymentMethodUsesBank(method) ? d.bankId : "",
                      referenceNo: method === "cheque" || method === "cash" ? "" : d.referenceNo,
                      chequeNo: method === "cheque" ? d.chequeNo : "",
                      chequeDate: method === "cheque" ? d.chequeDate : "",
                    }));
                  }}
                >
                  <SelectTrigger className="glass-input h-10">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_PAYMENT_METHODS.map((method) => (
                      <SelectItem key={method.value} value={method.value}>
                        {method.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {draft.paymentMethod ? (
                  <p className="text-[11px] text-muted-foreground">
                    Account/Channel: {paymentMethodMeta(draft.paymentMethod)?.channel}
                  </p>
                ) : null}
              </div>

              {draft.paymentMethod && !paymentMethodUsesBank(draft.paymentMethod) ? (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Account / Channel</Label>
                  <Input className="glass-input h-10" value="Cash" disabled />
                </div>
              ) : null}

              {paymentMethodUsesBank(draft.paymentMethod) ? (
                <div
                  className={cn(
                    "md:col-span-2 grid grid-cols-1 gap-4",
                    draft.paymentMethod === "cheque" ? "md:grid-cols-3" : "md:grid-cols-2",
                  )}
                >
                  <div className="space-y-2 min-w-0">
                    <Label className="text-xs text-muted-foreground">
                      Bank <span className="text-destructive">*</span>
                    </Label>
                    <ExpenseBankSelect
                      value={draft.bankId}
                      banks={banks}
                      onChange={(id) => setDraft((d) => ({ ...d, bankId: id }))}
                    />
                    {banks.length === 0 ? (
                      <p className="text-[11px] text-destructive">No banks found. Add a bank first.</p>
                    ) : null}
                  </div>

                  {draft.paymentMethod === "cheque" ? (
                    <>
                      <div className="space-y-2 min-w-0">
                        <Label className="text-xs text-muted-foreground">
                          Cheque # <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          className="glass-input h-10"
                          value={draft.chequeNo}
                          onChange={(e) => setDraft((d) => ({ ...d, chequeNo: e.target.value }))}
                          placeholder="Cheque number"
                        />
                      </div>
                      <div className="space-y-2 min-w-0">
                        <Label className="text-xs text-muted-foreground">Cheque date</Label>
                        <DatePartsPicker
                          value={draft.chequeDate}
                          onChange={(iso) => setDraft((prev) => ({ ...prev, chequeDate: normalizeExpenseDate(iso) }))}
                          fromYear={1980}
                          toYear={new Date().getFullYear() + 1}
                          placeholder="Optional"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2 min-w-0">
                      <Label className="text-xs text-muted-foreground">
                        {paymentMethodMeta(draft.paymentMethod)?.referenceLabel}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        className="glass-input h-10"
                        value={draft.referenceNo}
                        onChange={(e) => setDraft((d) => ({ ...d, referenceNo: e.target.value }))}
                        placeholder={paymentMethodMeta(draft.paymentMethod)?.referenceLabel ?? "Reference"}
                      />
                    </div>
                  )}
                </div>
              ) : null}

              <div className="space-y-2 md:col-span-2">
                <Label className="text-xs text-muted-foreground">Description</Label>
                <Input
                  className="glass-input h-10"
                  value={draft.description}
                  onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                  placeholder="Optional notes (rent, fuel, etc.)"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="glass" onClick={closeModal} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                variant="premium"
                onClick={() => void saveExpense()}
                disabled={isSaving || (editing ? !canUpdate : !canCreate)}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by subcategory, category, description, date, amount…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter expenses</SheetTitle>
                  <SheetDescription>Subcategory, payment method, amount range, and created date.</SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Expense</p>

                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Subcategory</Label>
                      <ExpenseSubcategorySelect
                        value={filterCategoryId}
                        subcategories={categories}
                        onChange={setFilterCategoryId}
                        includeAllOption
                        allValue="all"
                        allLabel="All"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Payment method</Label>
                      <Select value={filterPaymentMethod} onValueChange={(v) => setFilterPaymentMethod(v as typeof filterPaymentMethod)}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          {EXPENSE_PAYMENT_METHODS.map((method) => (
                            <SelectItem key={method.value} value={method.value}>
                              {method.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Min amount</Label>
                        <Input className="glass-input h-10" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} inputMode="decimal" placeholder="e.g. 100" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Max amount</Label>
                        <Input className="glass-input h-10" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} inputMode="decimal" placeholder="e.g. 5000" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn("glass-input h-10 w-full justify-start text-left font-normal", !filterCreatedDate && "text-muted-foreground")}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>

                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={resetSheetFilters}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect id="expenses-page-size-mobile" pageSize={pageSize} onPageSizeChange={setPageSize} options={PAGE_SIZE_OPTIONS} />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as ExpenseSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="amount_desc">Amount: high to low</SelectItem>
                  <SelectItem value="amount_asc">Amount: low to high</SelectItem>
                  <SelectItem value="updated_latest">Recently updated</SelectItem>
                  <SelectItem value="updated_oldest">Least recently updated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect id="expenses-page-size-desktop" pageSize={pageSize} onPageSizeChange={setPageSize} options={PAGE_SIZE_OPTIONS} />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[220px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as ExpenseSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="amount_desc">Amount: high to low</SelectItem>
                    <SelectItem value="amount_asc">Amount: low to high</SelectItem>
                    <SelectItem value="updated_latest">Recently updated</SelectItem>
                    <SelectItem value="updated_oldest">Least recently updated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {isBootstrapping ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading expenses…
          </div>
        ) : null}

        {!isBootstrapping && sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {expenses.length === 0
              ? canCreate
                ? "No expenses yet. Click “Add Expense” to create one."
                : "No expenses yet."
              : "No expenses match your search."}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {paginated.map((e, i) => (
              <motion.div key={e.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <ExpenseItemCard
                  expense={e}
                  labels={expenseLabels(e)}
                  variant="grid"
                  onEdit={canUpdate ? () => openEdit(e) : undefined}
                  onDelete={canDelete ? () => setDeleteTarget(e) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((e, i) => (
              <motion.div key={e.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                <ExpenseItemCard
                  expense={e}
                  labels={expenseLabels(e)}
                  variant="list"
                  onEdit={canUpdate ? () => openEdit(e) : undefined}
                  onDelete={canDelete ? () => setDeleteTarget(e) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this expense?"
          description={
            deleteTarget ? (
              <>
                Are you sure you want to delete <strong className="text-foreground font-semibold">{deleteTarget ? expenseLabels(deleteTarget).subcategory : "this expense"}</strong>?
                This cannot be undone.
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmDelete()}
        />
      </div>
    </Layout>
  );
};

export default Expenses;

